import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../api-client/APIClient";
import { useForm } from "react-hook-form";
import ContentLoader from "react-content-loader";
import SpinnerButton from "./SpinnerButton";
import { User, Comment } from "../../models/Entities";
import { Form, Col, Row, InputGroup } from "react-bootstrap";
import { fetchIntegrationIssueComments } from "../integrations-management/utils/IntegrationsManagementAPIClient";
import { getCapitalizedTimeFromNow } from "./utils/SharedComponentUtils";
import { UserAvatar, UserAvatarSize } from "./MergeAvatars";
import { spectrum } from "../../styles/theme";
import DeprecatedH5 from "../../deprecated/DeprecatedH5";

const commentStyle = {
  overflow: "hidden",
  overflowWrap: "break-word",
  height: "40px",
};

interface CommentsCardProps {
  integrationID?: string;
  integrationIssueID?: string;
}

export function Comments({ integrationID, integrationIssueID }: CommentsCardProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { register, handleSubmit, reset } = useForm();
  const [comments, setComments] = useState<undefined | Comment[]>();

  useEffect(() => {
    fetchComments();
  }, []);

  function fetchComments() {
    if (integrationID) {
      fetchWithAuth({
        path: `/integrations/${integrationID}/comments`,
        method: "GET",
        onResponse: (data) => {
          setComments(data.results);
        },
      });
    }
    if (integrationIssueID) {
      fetchIntegrationIssueComments({
        integrationIssueID,
        onSuccess: (data: { results: Comment[] }) => {
          setComments(data.results);
        },
      });
    }
  }

  const onSubmit = (data: { body: any }) => {
    setIsLoading(true);

    if (integrationID) {
      fetchWithAuth({
        path: `/integrations/${integrationID}/comments`,
        method: "POST",
        body: { body: data.body },
        onResponse: (data) => {
          comments && comments.unshift(data);
          reset();
          fetchComments();
          setIsLoading(false);
        },
      });
    }
    if (integrationIssueID) {
      fetchWithAuth({
        path: `/integrations/issues/${integrationIssueID}/comments`,
        method: "POST",
        body: { body: data.body },
        onResponse: (data) => {
          comments && comments.unshift(data);
          reset();
          fetchComments();
          setIsLoading(false);
        },
      });
    }
  };

  return (
    <>
      <div className="card-header">
        <Row>
          <Form className="form-inline w-100" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <Form.Group className="w-100">
              <Col>
                <InputGroup className="input-group input-group-flush">
                  <input
                    className="form-control form-control-flush w-100"
                    data-toggle="autosize"
                    placeholder="Leave a comment..."
                    name="body"
                    type="text"
                    style={commentStyle as any}
                    ref={register({
                      required: true,
                      minLength: 1,
                    })}
                  />
                </InputGroup>
              </Col>
              <Col className="col-auto align-self-end">
                <SpinnerButton
                  text="Submit"
                  isLoading={isLoading}
                  className="btn btn-sm btn-white btn-sm deprecated-mb-2"
                />
              </Col>
            </Form.Group>
          </Form>
        </Row>
      </div>
      <div className="card-body blue-modal-card">
        {comments ? (
          comments.map((comment) => <CommentRow comment={comment} key={comment.id} />)
        ) : (
          <>
            <ContentLoader
              speed={1.4}
              width={290}
              height={381}
              viewBox="0 0 290 381"
              backgroundColor={spectrum.gray0}
              foregroundColor={spectrum.gray10}
            >
              <rect x="81" y="37" rx="3" ry="3" width="162" height="23" />
              <rect x="81" y="15" rx="3" ry="3" width="162" height="15" />
              <circle cx="22" cy="28" r="21" />
              <rect x="81" y="124" rx="3" ry="3" width="162" height="23" />
              <rect x="81" y="102" rx="3" ry="3" width="162" height="15" />
              <circle cx="22" cy="115" r="21" />
              <rect x="81" y="211" rx="3" ry="3" width="162" height="23" />
              <rect x="81" y="189" rx="3" ry="3" width="162" height="15" />
              <circle cx="22" cy="202" r="21" />
              <rect x="81" y="298" rx="3" ry="3" width="162" height="23" />
              <rect x="81" y="276" rx="3" ry="3" width="162" height="15" />
              <circle cx="22" cy="289" r="21" />
              <rect x="81" y="385" rx="3" ry="3" width="157" height="23" />
              <rect x="81" y="363" rx="3" ry="3" width="162" height="15" />
              <circle cx="22" cy="376" r="21" />
            </ContentLoader>
          </>
        )}
      </div>
    </>
  );
}

interface CommentRowProps {
  comment: {
    id: string;
    created_at: string;
    body: string;
    user: User;
  };
}

export function CommentRow(props: CommentRowProps) {
  return (
    <div className="comment deprecated-mb-3" key={props.comment.id}>
      <Row>
        <Col className="col-auto">
          <UserAvatar user={props.comment.user} size={UserAvatarSize.sm} />
        </Col>
        <Col className="ml-n2">
          <div className="comment-body">
            <Row>
              <Col>
                <DeprecatedH5 className="comment-title">{props.comment.user.name}</DeprecatedH5>
              </Col>
              <Col className="col-auto">
                <time className="comment-time">
                  {getCapitalizedTimeFromNow(props.comment.created_at)}
                </time>
              </Col>
            </Row>
            <p className="comment-text text-break">{props.comment.body}</p>
          </div>
        </Col>
      </Row>
    </div>
  );
}
